/**
 * Stückzahl je Bestelleinheit eines Produkts, als Text für die Oberfläche.
 *
 * Quelle sind zwei Felder aus dem Artikelstamm: `packagingUnit` (Anzeigename der Einheit, z. B.
 * „Packung") und `totalUnitsPerPackagingUnit` (Stück in EINER Bestelleinheit). Beide befüllt nur der
 * Katalog-Import, und nur, wenn der Lieferanten-Feed sie mitliefert. Beide Apps reichen ihr eigenes
 * Modell hinein, deshalb der strukturelle Typ.
 *
 * <p><b>Die Zahl ist verlässlich, der Name der Einheit nicht.</b> Der Hartmann-Import setzt die Einheit
 * pauschal auf „Stück", auch bei Beuteln mit 24 Vorlagen (PROD 2026-09-23: rund 1.100 Hartmann-Produkte
 * heißen „Stück" und enthalten mehr als eins). Aus „Einheit mit Zahl" würde dort „Stück mit 24 Stück".
 * Deshalb heißt es bei „Stück" und bei fehlender Einheit „24 Stück je Einheit", im selben Wort wie der
 * Preis „/ Einheit". Korrigiert wird die Einheit im Import, nicht hier (Entscheidung 2026-09-23).
 *
 * <p><b>Eine Menge zählt Einheiten, nicht Stück.</b> „3" im Warenkorb eines Beutels mit 24 Vorlagen
 * sind 72 Stück; {@link stueckGesamt} rechnet das aus.
 */
export interface Verpackungsangabe {
  readonly packagingUnit?: string | null
  /** Enum-Code der Einheit, wo das Modell ihn trägt (Leistungserbringer-Portal). Schlägt den Namen. */
  readonly packagingUnitCode?: string | null
  readonly totalUnitsPerPackagingUnit?: number | null
}

const ZAHL = new Intl.NumberFormat('de-DE')

function stueck(anzahl: number): string {
  return `${ZAHL.format(anzahl)} Stück`
}

/** Stück je Einheit, aber nur, wenn es mehr als eins ist: „1 Stück je Einheit" sagt nichts. */
function stueckJeEinheit(p: Verpackungsangabe): number | undefined {
  const anzahl = p.totalUnitsPerPackagingUnit
  return anzahl != null && anzahl > 1 ? anzahl : undefined
}

function heisstStueck(p: Verpackungsangabe): boolean {
  return p.packagingUnitCode ? p.packagingUnitCode === 'STUECK' : p.packagingUnit === 'Stück'
}

/**
 * „Packung mit 20 Stück", „24 Stück je Einheit" oder nur der Name der Einheit, wenn keine Stückzahl
 * über eins gepflegt ist. `undefined`, wenn gar nichts gepflegt ist.
 */
export function verpackungseinheitText(p: Verpackungsangabe | null | undefined): string | undefined {
  if (!p) {
    return undefined
  }
  const einheit = p.packagingUnit || undefined
  const anzahl = stueckJeEinheit(p)
  if (anzahl === undefined) {
    return einheit
  }
  if (!einheit || heisstStueck(p)) {
    return `${stueck(anzahl)} je Einheit`
  }
  return `${einheit} mit ${stueck(anzahl)}`
}

/**
 * Stück in `menge` Einheiten. `undefined`, wenn das Produkt keine Stückzahl über eins trägt (dann ist
 * die Menge schon die Stückzahl oder es gibt keine) oder die Menge nichts ergibt.
 */
export function stueckGesamt(p: Verpackungsangabe | null | undefined, menge: number | null | undefined): number | undefined {
  const anzahl = p ? stueckJeEinheit(p) : undefined
  if (anzahl === undefined || menge == null || menge <= 0) {
    return undefined
  }
  return anzahl * menge
}

/** {@link stueckGesamt} als „72 Stück". */
export function stueckGesamtText(p: Verpackungsangabe | null | undefined, menge: number | null | undefined): string | undefined {
  const anzahl = stueckGesamt(p, menge)
  return anzahl === undefined ? undefined : stueck(anzahl)
}
